'use client'

import { UTCTimestamp } from 'lightweight-charts'

/**
 * Creates safe data points for vertical lines by ensuring unique timestamps
 * This prevents the "data must be asc ordered by time" error in lightweight-charts
 */
export const createVerticalLineData = (
  baseTime: number,
  bottomPrice: number,
  topPrice: number
): Array<{ time: UTCTimestamp; value: number }> => {
  return [
    {
      time: (baseTime - 1) as UTCTimestamp,
      value: bottomPrice
    },
    {
      time: (baseTime + 1) as UTCTimestamp,
      value: topPrice
    }
  ]
}

/**
 * Creates safe data points for horizontal lines by ensuring unique timestamps
 */
export const createHorizontalLineData = (
  startTime: number,
  endTime: number,
  price: number
): Array<{ time: UTCTimestamp; value: number }> => {
  // Ensure times are different and ascending
  const safeStartTime = Math.min(startTime, endTime)
  const safeEndTime = Math.max(startTime, endTime)
  
  // If times are equal, add small offset
  const finalEndTime = safeStartTime === safeEndTime ? safeEndTime + 1 : safeEndTime
  
  return [
    {
      time: safeStartTime as UTCTimestamp,
      value: price
    },
    {
      time: finalEndTime as UTCTimestamp,
      value: price
    }
  ]
}

/**
 * Creates safe data points for pattern necklines
 */
export const createNecklineData = (
  point1: { time: number; price: number },
  point2: { time: number; price: number }
): Array<{ time: UTCTimestamp; value: number }> => {
  // Ensure ascending time order
  const [firstPoint, secondPoint] = point1.time <= point2.time ? 
    [point1, point2] : [point2, point1]
  
  // If times are equal, add small offset to second point
  const safeSecondTime = firstPoint.time === secondPoint.time ? 
    secondPoint.time + 1 : secondPoint.time
  
  return [
    {
      time: firstPoint.time as UTCTimestamp,
      value: firstPoint.price
    },
    {
      time: safeSecondTime as UTCTimestamp,
      value: secondPoint.price
    }
  ]
}

/**
 * Sorts and validates chart data to ensure proper time ordering
 */
export const validateChartData = (
  data: Array<{ time: number; value: number }>
): Array<{ time: UTCTimestamp; value: number }> => {
  // Sort by time
  const sortedData = [...data].sort((a, b) => a.time - b.time)
  
  // Remove duplicates and ensure strictly ascending times
  const uniqueData: Array<{ time: UTCTimestamp; value: number }> = []
  let lastTime = 0
  
  for (const point of sortedData) {
    const safeTime = point.time > lastTime ? point.time : lastTime + 1
    uniqueData.push({
      time: safeTime as UTCTimestamp,
      value: point.value
    })
    lastTime = safeTime
  }
  
  return uniqueData
}

/**
 * Helper to create projection lines extending into the future
 */
export const createProjectionLineData = (
  startTime: number,
  targetPrice: number,
  daysAhead: number = 30
): Array<{ time: UTCTimestamp; value: number }> => {
  const endTime = startTime + (86400 * daysAhead) // Add days in seconds
  
  return [
    {
      time: startTime as UTCTimestamp,
      value: targetPrice
    },
    {
      time: endTime as UTCTimestamp,
      value: targetPrice
    }
  ]
} 